import { useEffect, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import clsx from 'clsx'
import { isEmptyObject } from 'es-toolkit'
import type { RxDocument } from 'rxdb'

import type { Card } from '~/db/types'
import { useSettingsStore } from '~/stores/settings'

import { useExpandedStore } from '../../stores/expanded'
import { CardWithAnimation } from '../animated-edit-card'
import { COLLAPSED_CARD_HEIGHT, EXPANDED_CARD_HEIGHT } from '../edit-card/const'

import { COLUMN_GAP_PX } from './const'
import { useResponsiveColumnCount, useScrollToActiveCard } from './hooks'
import { type ColumnCount, columns, type StatelessListProps } from './types'
import { getTwoColumnStyles, hasExpandedRowSibling, imperativeRepositionFactory, syncListItemPosition } from './utils'

export const StatelessList = ({
  activeCardId,
  cards,
  className,
  lastInsertedId,
  ref,
  activeCardRef,
  ...cardProps
}: StatelessListProps) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const imperativeRepositionRef = useRef<Record<string, (height: number) => void>>({})
  const skipOthersRepositionRef = useRef(false)
  const ulRef = useRef<HTMLUListElement>(null)
  const columnCount = useResponsiveColumnCount()

  const rowCount = Math.ceil((cards?.length ?? 0) / columnCount)

  // oxlint-disable-next-line react/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: (rowIndex: number) => {
      const rowHeights = Array.from({ length: columnCount }, (_, columnIndex) => {
        const card = cards?.[rowIndex * columnCount + columnIndex]
        if (!card) return 0

        return useExpandedStore.getState().isExpanded(card.id) ? EXPANDED_CARD_HEIGHT : COLLAPSED_CARD_HEIGHT
      })

      return Math.max(...rowHeights)
    },
    gap: COLUMN_GAP_PX,
  })

  useEffect(() => rowVirtualizer.measure(), [columnCount, rowVirtualizer])

  useScrollToActiveCard({ cards, activeCardId, rowVirtualizer })

  const getVisibleCards = () => {
    const rows = rowVirtualizer.getVirtualItems()

    const startRowIndex = rows.at(0)?.index ?? 0
    const endRowIndex = rows.at(-1)?.index ?? 0
    const startCardsIndex = startRowIndex * columnCount
    const endCardsIndex = endRowIndex * columnCount + columnCount

    return cards?.slice(startCardsIndex, endCardsIndex) ?? []
  }

  const onExpandingScrolling = (container: HTMLDivElement, cardElement: HTMLElement) => {
    const containerPaddingBottom = parseInt(getComputedStyle(container).paddingBottom)
    const cardsToContainerBottomDiff =
      cardElement.getBoundingClientRect().bottom - container.getBoundingClientRect().bottom - containerPaddingBottom
    if (cardsToContainerBottomDiff > 0) container.scrollTo({ top: container.scrollTop + cardsToContainerBottomDiff })
  }

  const onExpandHeightAnimationIteration = (card: Card, height: number) => {
    const container = parentRef.current
    if (!container) return console.error({ message: 'Container not found' })

    const cardElement = document.getElementById(card.id)
    if (!cardElement) return console.error({ message: 'Card element not found', cardId: card.id })

    // scroll to keep the card in view
    const isExpanding = useExpandedStore.getState().isExpanded(card.id)
    if (isExpanding) onExpandingScrolling(container, cardElement)

    const imperativeReposition = imperativeRepositionRef.current[card.id]
    if (imperativeReposition) return imperativeReposition(height)

    const cardLiWrapper = cardElement.closest('li')
    if (!cardLiWrapper) return console.error({ message: 'Card li wrapper not found', cardId: card.id })

    if (columnCount === 2 && hasExpandedRowSibling(card.id, cards, columnCount)) {
      skipOthersRepositionRef.current = true
      imperativeRepositionRef.current[card.id] = (nextHeight: number) => {
        cardLiWrapper.style.height = `${nextHeight}px`
      }
      imperativeRepositionRef.current[card.id]?.(height)
      return
    }

    imperativeRepositionRef.current[card.id] = imperativeRepositionFactory({
      visibleCards: getVisibleCards(),
      allCards: cards,
      cardElement,
      cardId: card.id,
      columnCount,
    })
    imperativeRepositionRef.current[card.id]?.(height)
  }

  const onExpandAnimationComplete = () => {
    if (isEmptyObject(imperativeRepositionRef.current)) return

    imperativeRepositionRef.current = {}

    const defaultOptions = rowVirtualizer.options
    // Because scroll does not apply instantly, we need to overscan to all rendered cards
    rowVirtualizer.setOptions({ ...defaultOptions, overscan: 3 })

    rowVirtualizer.measure()

    if (skipOthersRepositionRef.current) {
      skipOthersRepositionRef.current = false
      rowVirtualizer.setOptions(defaultOptions)

      return
    }

    rowVirtualizer.getVirtualItems().forEach(virtualRow => {
      const rowStartIndex = virtualRow.index * columnCount

      for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
        const card = cards?.[rowStartIndex + columnIndex]
        // Some cards might be not rendered by virtualizer
        if (!card || !document.getElementById(card.id)) continue

        syncListItemPosition({ virtualRow, columnNumber: columns[columnIndex] ?? 1, cardId: card.id, columnCount })
      }
    })
    if (ulRef.current) ulRef.current.style.height = `${rowVirtualizer.getTotalSize()}px`

    rowVirtualizer.setOptions(defaultOptions)
  }

  const onCollapse = () => {
    const lastCard = cards?.at(-1)
    if (!lastCard) return

    const lastCardElement = document.getElementById(lastCard.id)
    if (!lastCardElement) return

    const container = parentRef.current
    if (!container) return console.error({ message: 'Container not found' })

    const lastCardBottomAfterCollapse =
      lastCardElement.getBoundingClientRect().bottom - EXPANDED_CARD_HEIGHT + COLLAPSED_CARD_HEIGHT
    const containerPaddingBottom = parseInt(getComputedStyle(container).paddingBottom)
    const lastCardToContainerBottomDiff = lastCardBottomAfterCollapse - container.getBoundingClientRect().bottom

    const lastCardWillHaveGap = lastCardToContainerBottomDiff < containerPaddingBottom
    if (!lastCardWillHaveGap) return

    container.scrollTo({ top: container.scrollTop + lastCardToContainerBottomDiff, behavior: 'smooth' })
  }

  return (
    <div
      ref={node => {
        parentRef.current = node
        if (ref) ref.current = node
      }}
      className={clsx(
        'overflow-y-auto overflow-x-hidden px-1 py-3 max-md:flex-col md:flex-wrap md:content-start',
        className,
      )}
    >
      <ul
        ref={ulRef}
        className="relative m-0 w-full list-none p-0"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().flatMap(virtualRow => {
          const rowStartIndex = virtualRow.index * columnCount

          return Array.from({ length: columnCount }, (_, columnIndex) => {
            const card = cards?.[rowStartIndex + columnIndex]
            // Possible if the number of cards is odd
            if (!card) return null

            const isNew = card.id === lastInsertedId
            const isActive = card.id === activeCardId

            const columnNumber: ColumnCount = columns[columnIndex] ?? 1

            const styles = getTwoColumnStyles(columnNumber)
            return (
              <li
                key={card.id}
                className={clsx('absolute top-0', columnCount === 1 && 'left-0 w-full')}
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px) ${styles.translateXValue}`,
                  ...(columnCount === 2 && styles),
                }}
              >
                <CardWithAnimation
                  {...cardProps}
                  ref={isActive ? activeCardRef : undefined}
                  autoFocusLearnText={isActive && cardsFront === 'learn'}
                  autoFocusSourceText={isActive && cardsFront === 'source'}
                  card={card as RxDocument<Card>}
                  isActive={isActive}
                  isNew={isNew}
                  onCollapse={onCollapse}
                  onExpandAnimationComplete={onExpandAnimationComplete}
                  onExpandHeightAnimationIteration={onExpandHeightAnimationIteration}
                />
              </li>
            )
          })
        })}
      </ul>
    </div>
  )
}
